
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, CancelTokenSource, Method } from 'axios';
import * as queryString from 'query-string';

import { UnauthenticatedUserHandler } from '~src/authentication/services/UnauthenticatedUserHandler';

import { getAppStore } from '../../app/AppStore';
import { SessionSiteSelectors } from '../../app/selectors/SessionInfoSelectors';
import {
  getSessionManagementServiceInstance
} from '../../sessionManagement/SessionManagementService';

/**
 * Config for the REST API request; agnostic of the underlying HTTP library
 */
export type RestApiRequestConfig<TRequestBody, TQueryParams extends Object = {}> = {
  restApiVersion: string;
  httpMethod: Method;
  endpoint: string;
  requestBody: TRequestBody;
  serverWideRequest?: boolean; // defaults to false
  queryParams?: TQueryParams;
  headers?: { [index: string]: string };
  cancelToken?: CancelTokenSource;  // Usage https://github.com/axios/axios#cancellation
  onUploadProgress?: (progressEvent: unknown) => void;
  onDownloadProgress?: (progressEvent: unknown) => void;
};

export enum RestErrorSource {
  Server,
  Cancelled,
}

export type RestApiErrorResponse = {
  readonly errorSource?: RestErrorSource;
  readonly data: RestApiErrorData;
  readonly status: number;
  readonly statusText: string;
};

export type RestApiErrorData = {
  readonly error: RestApiError;
};

export type RestApiError = {
  readonly summary: string;
  readonly detail: string;
  readonly code: string;
};

export function getErrorSourceFromRestError(error: any): RestErrorSource {
  if (error?.errorSource != null) {
    return error.errorSource;
  }

  return RestErrorSource.Server;
}

export type JsonRestApiRequestConfig = Omit<RestApiRequestConfig<unknown, {}>, 'requestBody' | 'headers' | 'queryParams'>;

export function makeRestApiRequest<TRequestBody, TResult, TQueryParams extends Object = {}>(
  config: RestApiRequestConfig<TRequestBody, TQueryParams>
): PromiseLike<TResult> {

  const siteLuid = SessionSiteSelectors.siteLuid(getAppStore().getState());

  let url = config.serverWideRequest
    ? `api/${config.restApiVersion}/${config.endpoint}`
    : `api/${config.restApiVersion}/sites/${siteLuid}/${config.endpoint}`;

  if (config.queryParams) {
    url += `?${queryString.stringify(config.queryParams)}`;
  }

  const axiosConfig: AxiosRequestConfig = {
    method: config.httpMethod,
    data: config.requestBody,
    url: url,
    headers: config.headers,
    onUploadProgress: config.onUploadProgress,
    onDownloadProgress: config.onDownloadProgress,
    cancelToken: config.cancelToken ? config.cancelToken.token : null
  };

  return getSessionManagementServiceInstance().sendRequestWithinSession(
    () => makeAxiosRequest<TResult>(axiosConfig)
  );
}

export function makeJsonRestApiRequest<TRequest, TResult, TQueryParams extends Object = {}>({ config, request, queryParams }: {
  config: JsonRestApiRequestConfig;
  request: TRequest;
  queryParams?: TQueryParams;
}): PromiseLike<TResult> {

  const isRequestDefined = (request !== null && request !== undefined);
  const requestString = isRequestDefined ? JSON.stringify(request) : null;

  return makeRestApiRequest({
    ...config,
    queryParams: queryParams,
    requestBody: requestString,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  });
}

function makeAxiosRequest<TResult>(requestConfig: AxiosRequestConfig): Promise<TResult> {
  return axios(requestConfig).then(
    (successResponse: AxiosResponse<TResult>) => {
      return successResponse.data;
    },
    (errorResponse: AxiosError<RestApiErrorData>) => {
      if (axios.isCancel(errorResponse)) {
        return Promise.reject({
          errorSource: RestErrorSource.Cancelled,
        });
      }

      const innerResponse: RestApiErrorResponse = errorResponse.response;
      const error: RestApiError = innerResponse.data.error;

      if (innerResponse.status === 401) {
        const reason = parseInt(error.code, 10);
        UnauthenticatedUserHandler.handle401(reason);
      }

      return Promise.reject({
        ...innerResponse,
        errorSource: RestErrorSource.Server,
      });
    }
  );
}
